import { useState, useEffect } from 'react'
import { Heart } from 'lucide-react'
import { Product } from '../../types'
import { useAuth } from '../../contexts/AuthContext'
import { Link } from 'react-router-dom'

interface WishlistButtonProps {
  product: Product
  className?: string
}

// Basit wishlist - localStorage kullanıyor
const useWishlist = () => {
  const getWishlist = (): string[] => {
    try {
      const saved = localStorage.getItem('petunya-wishlist')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  }

  const addToWishlist = (productId: string) => {
    const wishlist = getWishlist()
    if (!wishlist.includes(productId)) {
      const newWishlist = [...wishlist, productId]
      localStorage.setItem('petunya-wishlist', JSON.stringify(newWishlist))
    }
  }

  const removeFromWishlist = (productId: string) => {
    const wishlist = getWishlist()
    const newWishlist = wishlist.filter(id => id !== productId)
    localStorage.setItem('petunya-wishlist', JSON.stringify(newWishlist))
  }

  const isInWishlist = (productId: string): boolean => {
    return getWishlist().includes(productId)
  }

  return { addToWishlist, removeFromWishlist, isInWishlist }
}

export default function WishlistButton({ product, className = "" }: WishlistButtonProps) {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist()
  const [isFavorite, setIsFavorite] = useState(isInWishlist(product.id))
  const [isAnimating, setIsAnimating] = useState(false)
  const { isAuthenticated } = useAuth()

  // Update state when component mounts or product changes
  useEffect(() => {
    setIsFavorite(isInWishlist(product.id))
  }, [product.id])

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Giriş yapmamış kullanıcıları login sayfasına yönlendir
    if (!isAuthenticated) {
      return
    }
    
    setIsAnimating(true)
    
    if (isFavorite) {
      removeFromWishlist(product.id)
      setIsFavorite(false)
    } else {
      addToWishlist(product.id)
      setIsFavorite(true)
    }
    
    // Force re-render by checking localStorage again
    setTimeout(() => {
      setIsAnimating(false)
      setIsFavorite(isInWishlist(product.id))
    }, 300)
  }

  // Giriş yapmamış kullanıcılar için login linkine yönlendirme
  if (!isAuthenticated) {
    return (
      <Link to="/auth/login">
        <button
          className={`group relative p-2 rounded-xl transition-all duration-300 transform hover:scale-110 z-10 bg-white/80 text-gray-400 hover:bg-white hover:text-red-500 ${className}`}
          title="Favorilere eklemek için giriş yapın"
          style={{ pointerEvents: 'auto' }}
        >
          <Heart className="w-5 h-5 transition-all duration-300 hover:fill-current" />
        </button>
      </Link>
    )
  }

  return (
    <button
      onClick={handleToggle}
      className={`group relative p-2 rounded-xl transition-all duration-300 transform hover:scale-110 z-10 ${
        isFavorite 
          ? 'bg-red-50 text-red-500 hover:bg-red-100' 
          : 'bg-white/80 text-gray-400 hover:bg-white hover:text-red-500'
      } ${className}`}
      title={isFavorite ? 'Favorilerden çıkar' : 'Favorilere ekle'}
      style={{ pointerEvents: 'auto' }}
    >
      <Heart 
        className={`w-5 h-5 transition-all duration-300 ${
          isFavorite ? 'fill-current' : 'hover:fill-current'
        } ${isAnimating ? 'animate-bounce' : ''}`} 
      />
      
      {/* Pulse effect when adding */}
      {isAnimating && isFavorite && (
        <div className="absolute inset-0 rounded-xl bg-red-500/20 animate-ping"></div>
      )}
    </button>
  )
}